import { NotionBlock } from '@Models';

// Helper functions for media handling
export const isYouTubeUrl = (url: string): boolean => /youtu\.be|youtube\.com/.test(url);

export const getYouTubeEmbedUrl = (url: string): string => {
  const regExp = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
  const match = url.match(regExp);
  if (!match) return url;
  const paramsMatch = url.match(/\?(.*)$/);
  const params = paramsMatch ? `?${paramsMatch[1]}` : '';
  return `https://www.youtube.com/embed/${match[1]}${params}`;
};

export const isVideoUrl = (url: string): boolean => {
  return (
    typeof url === 'string' &&
    (url.endsWith('.mp4') || url.endsWith('.mov') || url.includes('video') || url.includes('mov'))
  );
};

export const isImageUrl = (url: string): boolean => {
  return (typeof url === 'string' && /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url)) || url.includes('unsplash.com');
};

// Helper function to get background color class for tags in child_database
export const getTagColorClass = (color?: string | null): string => {
  const colorMap: { [key: string]: string } = {
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    purple: 'bg-purple-100 text-purple-700',
    pink: 'bg-pink-100 text-pink-700',
    orange: 'bg-orange-100 text-orange-700',
    brown: 'bg-[#9F765A]/30 text-[#9F765A]',
    gray: 'bg-gray-200 text-gray-600',
    default: 'bg-gray-200 text-gray-600',
  };
  return colorMap[color || 'default'] || colorMap.default;
};

// Helper function to get callout styles based on color
export const getCalloutStyles = (color?: string): string => {
  const styleMap: { [key: string]: string } = {
    gray_background: 'bg-gray-600/10',
    brown_background: 'bg-[#9F765A]/20',
    orange_background: 'bg-orange-600/20',
    yellow_background: 'bg-yellow-600/20',
    green_background: 'bg-green-600/20',
    blue_background: 'bg-blue-600/20',
    purple_background: 'bg-purple-600/20',
    pink_background: 'bg-pink-600/20',
    red_background: 'bg-red-600/20',
    default: 'outline-2 outline-gray-700',
  };
  return styleMap[color || ''] || '';
};

// Helper function to get text color class for rich text
export const getTextColorClass = (color: string, defaultColor = 'text-white'): string => {
  const colorMap: { [key: string]: string } = {
    red: 'text-red-400',
    blue: 'text-blue-400',
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    purple: 'text-purple-400',
    pink: 'text-pink-400',
    orange: 'text-orange-400',
    brown: 'text-[#9F765A]',
    gray: 'text-gray-600',
    default: defaultColor,
  };
  return colorMap[color] || colorMap.default;
};

// Helper function to format dates from Notion
export const formatNotionDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper function to group consecutive list items by type
export const groupChildrenByType = (children: NotionBlock[]) => {
  return children.reduce((acc, child) => {
    const lastGroup = acc[acc.length - 1];

    if (
      (child.type === 'numbered_list_item' || child.type === 'bulleted_list_item') &&
      lastGroup?.type === child.type
    ) {
      // Same type as last group, add to it
      lastGroup.items.push(child);
    } else {
      // Different type or not a list, create new group
      acc.push({ type: child.type, items: [child] });
    }
    return acc;
  }, [] as Array<{ type: string; items: NotionBlock[] }>);
};

// Helper function to create mouse resize handler for columns
export const createColumnResizeHandler = (
  index: number,
  columnWidths: number[],
  setColumnWidths: (widths: number[]) => void,
) => {
  return (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidths = [...columnWidths];

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const containerWidth = (moveEvent.target as HTMLElement).closest('.column-container')?.clientWidth || 1000;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const newWidths = [...startWidths];
      newWidths[index] = Math.max(20, Math.min(80, startWidths[index] + deltaPercent));
      newWidths[index + 1] = Math.max(20, Math.min(80, startWidths[index + 1] - deltaPercent));

      setColumnWidths(newWidths);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
};
